import { yupResolver } from '@hookform/resolvers/yup';
import Button from '@shared/components/Button/Button';
import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ReactComponent as LockPin } from 'assets/icons/LockPin.svg';
import { Schemas } from 'utils/yup/schemas/validationSchemas';

import { CreateSecurityCodeStepsEnum, useCreateSecurityCodeDataContext } from './CreateSecurityCodeContext';
import SecurityCodeModalBase from './SecurityCodeModalBase';
import { SECURITY_CODE_LENGTH } from '../securityCodeConstants';
import SecurityCodeInput, { SecurityCodeInputHandle } from '../SecurityCodeInput/SecurityCodeInput';

type ConfirmSecurityCodeFormData = {
  securityCode: string;
  confirmSecurityCode: string;
};

const ConfirmPasscodeModal = () => {
  const { t } = useTranslation();
  const passcodeRef = useRef<SecurityCodeInputHandle>(null);
  const { pin, createSecurityCode, isCreating, errorKey, clearError, setCurrentStep, close } =
    useCreateSecurityCodeDataContext();

  const {
    handleSubmit,
    control,
    resetField,
    watch,
    formState: { errors },
  } = useForm<ConfirmSecurityCodeFormData>({
    mode: 'all',
    resolver: yupResolver(Schemas.getSecurityCodeValidation()),
    defaultValues: { securityCode: pin, confirmSecurityCode: '' },
  });

  const onSubmit = async (data: ConfirmSecurityCodeFormData) => {
    try {
      await createSecurityCode(data.securityCode);
      setCurrentStep(CreateSecurityCodeStepsEnum.SUCCESSFULLY_CREATED_PASSCODE);
    } catch {
      resetField('confirmSecurityCode', { defaultValue: '' });
      passcodeRef.current?.focusFirst();
    }
  };

  const onInvalid = (formErrors: { confirmSecurityCode?: { message?: string } }) => {
    if (formErrors.confirmSecurityCode) {
      resetField('confirmSecurityCode', { defaultValue: '' });
      passcodeRef.current?.focusFirst();
    }
  };

  const confirmPinError = errors.confirmSecurityCode?.message;
  const displayError = confirmPinError || (errorKey ? t(errorKey) : null);
  const confirmSecurityCode = watch('confirmSecurityCode') ?? '';

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (!confirmSecurityCode.trim()) {
        e.preventDefault();
        e.stopPropagation();
        setCurrentStep(CreateSecurityCodeStepsEnum.SETUP_PASSCODE);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [confirmSecurityCode, setCurrentStep]);

  return (
    <SecurityCodeModalBase
      isVisible
      onClose={close}
      icon={LockPin}
      title={t('verification.verify_security_code_title')}
      footer={
        <>
          <Button
            variant="secondaryTertiary"
            size="md"
            className="h-12 min-w-[105px] rounded-2xl px-6"
            onClick={() => setCurrentStep(CreateSecurityCodeStepsEnum.SETUP_PASSCODE)}
            disabled={isCreating}
          >
            {t('back')}
          </Button>
          <Button
            variant="primary"
            size="md"
            className="h-12 min-w-[157px] rounded-2xl px-6"
            type="submit"
            form="confirm-passcode-form"
            loading={isCreating}
            disabled={
              confirmSecurityCode.length !== SECURITY_CODE_LENGTH || isCreating || Boolean(errors.confirmSecurityCode)
            }
          >
            {t('verification.verify_security_code_cta')}
          </Button>
        </>
      }
    >
      <form id="confirm-passcode-form" onSubmit={handleSubmit(onSubmit, onInvalid)} className="w-full">
        <Controller
          name="confirmSecurityCode"
          control={control}
          render={({ field }) => (
            <SecurityCodeInput
              ref={passcodeRef}
              value={field.value}
              onChange={(value) => {
                field.onChange(value);
                if (errorKey) clearError();
              }}
              error={displayError}
              helperText={t('verification.verify_security_code_helper')}
              autoFocus
              disabled={isCreating}
            />
          )}
        />
      </form>
    </SecurityCodeModalBase>
  );
};

export default ConfirmPasscodeModal;
